import { Building2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ScopePicker } from "@/components/layout/ScopePicker";
import { useDealCaptureLookups } from "@/hooks/useDealCapture";
import { areaListPath, areaPath, entityPath, locate } from "@/lib/deal-capture/paths";
import { formatNumber } from "@/lib/format";
import type { SidebarSlotProps } from "@/lib/module-registry";
import { ENTITY_KIND_LABELS } from "@/types/deal-capture";

import { ALL_ID, MATCHES_ID, matches, pickerOptions } from "./picker-options";

/**
 * The Entity picker, while the area is Entities. Matches a legal entity's
 * short or legal name, A–Z by short name.
 */
export function EntityPicker({ onClose }: SidebarSlotProps) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { data, isLoading } = useDealCaptureLookups();
  const [query, setQuery] = useState("");
  const { recordId, page } = locate(pathname);
  const entities = useMemo(() => data?.entities ?? [], [data]);

  const matched = useMemo(
    () =>
      entities
        .filter((e) => matches(query, e.shortName, e.legalName))
        .sort((a, b) => a.shortName.localeCompare(b.shortName))
        .map((e) => ({
          id: e.id,
          label: e.shortName,
          hint: `${e.legalName} · ${ENTITY_KIND_LABELS[e.kind]}`,
        })),
    [entities, query],
  );
  const { options, footer } = pickerOptions(query, matched, entities.length, "All entities");

  const open = entities.find((e) => e.id === recordId);
  const params = new URLSearchParams(search);
  const listQuery = params.get("q") ?? "";
  const listType = params.get("type");
  const selected = open
    ? { id: open.id, label: `${open.shortName} · ${open.legalName}` }
    : page !== "list"
      ? null
      : listQuery
        ? { id: MATCHES_ID, label: `Matches for “${listQuery}”` }
        : listType === "customer" || listType === "trade"
          ? { id: ALL_ID, label: `${ENTITY_KIND_LABELS[listType]} entities` }
          : { id: ALL_ID, label: `All entities · ${formatNumber(entities.length)}` };

  return (
    <ScopePicker
      axis="Entity"
      icon={Building2}
      selected={selected}
      placeholder="Find an entity"
      searchPlaceholder={`Search ${formatNumber(entities.length)} entities by name`}
      emptyMessage="No entities match that name."
      options={options}
      isLoading={isLoading}
      footer={footer}
      onSearch={setQuery}
      onSelect={(id) => {
        if (id === ALL_ID) navigate(areaListPath("entities"));
        else if (id === MATCHES_ID) {
          navigate(`${areaListPath("entities")}?${new URLSearchParams({ q: query.trim() })}`);
        } else navigate(entityPath(id));
        onClose();
      }}
      onClear={() => {
        navigate(areaPath("entities"));
        onClose();
      }}
    />
  );
}
